import { useCallback, useEffect, useRef } from 'react';
import { useData } from './useData';

const DEFAULT_STATE = {
  visible: false,
  title: 'Preparing your website…',
  description:
    'We’re setting everything up. This usually takes less than a minute.',
  percent: 0,
};

export const useLoadingOverlay = () => {
  const [, setData] = useData((s) => s);
  const progressTimerRef = useRef();
  const overlayStateRef = useRef(DEFAULT_STATE);

  const clearTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = undefined;
    }
  };

  const showLoading = useCallback(
    (options = {}) => {
      clearTimer();
      overlayStateRef.current = { ...DEFAULT_STATE, ...options, visible: true };
      setData({ loadingOverlay: overlayStateRef.current });
      const startPercent =
        typeof options.percent === 'number' ? options.percent : 0;
      let current = Math.max(0, Math.min(95, startPercent));
      progressTimerRef.current = setInterval(() => {
        current = Math.min(95, current + Math.random() * 3 + 1);
        overlayStateRef.current = {
          ...overlayStateRef.current,
          percent: current,
        };
        setData({ loadingOverlay: overlayStateRef.current });
      }, 500);
    },
    [setData]
  );

  const updateLoading = useCallback(
    (partial) => {
      clearTimer();
      overlayStateRef.current = { ...overlayStateRef.current, ...partial };
      setData({ loadingOverlay: overlayStateRef.current });
    },
    [setData]
  );

  const hideLoading = useCallback(() => {
    clearTimer();
    overlayStateRef.current = {
      ...overlayStateRef.current,
      visible: false,
      percent: 100,
    };
    setData({ loadingOverlay: overlayStateRef.current });
  }, [setData]);

  useEffect(() => () => clearTimer(), []);

  return { showLoading, updateLoading, hideLoading };
};
